"use client";

import React from "react";
import { CalendarPlus, Ticket, MapPin, Clock } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
import { PostsProvider, usePosts } from "@/integrations/wordpress/WordPressPostsProvider";
import { WP_Query } from "@/integrations/wordpress/wp_query";
const stripHtml = (html: string) => {
  if (typeof window === "undefined") return html.replace(/<[^>]*>?/gm, "");
  const doc = new DOMParser().parseFromString(html, "text/html");
  return doc.body.textContent || "";
};
const generateGCalLink = (title: string, excerpt: string = "", dateString: string = "") => {
  const text = encodeURIComponent(title);
  const details = encodeURIComponent(stripHtml(excerpt).substring(0, 500));
  const location = encodeURIComponent("Fort Lauderdale, FL");
  let dates = "";
  if (dateString) {
    const d = new Date(dateString);
    const pad = (n: number) => n < 10 ? "0" + n : n;
    const start = `${d.getUTCFullYear()}${pad(d.getUTCMonth() + 1)}${pad(d.getUTCDate())}T${pad(d.getUTCHours())}${pad(d.getUTCMinutes())}${pad(d.getUTCSeconds())}Z`;
    const dEnd = new Date(d.getTime() + 3 * 60 * 60 * 1000);
    const end = `${dEnd.getUTCFullYear()}${pad(dEnd.getUTCMonth() + 1)}${pad(dEnd.getUTCDate())}T${pad(dEnd.getUTCHours())}${pad(dEnd.getUTCMinutes())}${pad(dEnd.getUTCSeconds())}Z`;
    dates = `&dates=${start}/${end}`;
  }
  return `https://calendar.google.com/calendar/render?action=TEMPLATE&text=${text}&details=${details}&location=${location}${dates}`;
};
function EventsList() {
  const {
    posts,
    loading,
    error
  } = usePosts();
  if (loading) {
    return <div className="w-full max-w-5xl mx-auto flex flex-col border-t border-border/30">
        {[...Array(4)].map((_, i) => <div key={i} data-index={i} className="flex flex-col md:flex-row border-b border-border/30 p-6 md:p-10 animate-pulse">
            <div className="md:w-48 shrink-0 h-24 bg-muted/20 rounded-md mb-6 md:mb-0 md:mr-10"></div>
            <div className="flex-1 space-y-4">
              <div className="h-8 bg-muted/20 rounded-md w-3/4"></div>
              <div className="h-4 bg-muted/20 rounded-md w-full"></div>
              <div className="h-4 bg-muted/20 rounded-md w-5/6"></div>
              <div className="flex gap-4 pt-4">
                <div className="h-10 bg-muted/20 rounded-md w-32"></div>
                <div className="h-10 bg-muted/20 rounded-md w-40"></div>
              </div>
            </div>
          </div>)}
      </div>;
  }
  if (error) {
    return <div className="text-center py-20 text-destructive font-mono">
        <p>System Error: Unable to retrieve event data.</p>
        <p className="text-sm opacity-70 mt-2">{error}</p>
      </div>;
  }
  if (posts.length === 0) {
    return <div className="text-center py-20 text-muted-foreground font-mono">
        <p>No upcoming events found in the database.</p>
      </div>;
  }
  return <div className="w-full max-w-5xl mx-auto flex flex-col border-t border-border/30 shadow-2xl shadow-black/50">
      {posts.map((post, i) => {
      const dateObj = new Date(post.date || Date.now());
      const day = dateObj.getDate().toString().padStart(2, "0");
      const month = dateObj.toLocaleString("default", {
        month: "short"
      }).toUpperCase();
      const time = dateObj.toLocaleTimeString("default", {
        hour: "numeric",
        minute: "2-digit"
      });
      return <article key={post.id} data-index={i} className={`group flex flex-col md:flex-row border-b border-border/30 transition-all duration-500 hover:bg-accent/5 ${i % 2 !== 0 ? "bg-[hsl(222,47%,7%)]" : "bg-transparent"}`}>
            {}
            <div className="md:w-56 shrink-0 flex flex-row md:flex-col items-center md:items-start justify-between md:justify-start p-6 md:p-10 border-b md:border-b-0 md:border-r border-border/30 relative overflow-hidden">
              <div className="absolute inset-0 bg-gradient-to-br from-primary/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
              
              <div className="flex flex-col items-center md:items-start relative z-10">
                <span className="font-mono text-6xl md:text-8xl font-black text-transparent bg-clip-text bg-gradient-to-b from-foreground to-muted-foreground/30 leading-none tracking-tighter">
                  {day}
                </span>
                <span className="font-mono text-xl md:text-2xl font-bold text-primary tracking-[0.2em] mt-2 md:mt-4">
                  {month}
                </span>
              </div>

              <div className="flex flex-col items-end md:items-start gap-2 mt-0 md:mt-8 text-muted-foreground font-mono text-sm relative z-10">
                <div className="flex items-center gap-2">
                  <Clock className="size-4 text-primary/70" />
                  <span>{time}</span>
                </div>
                <div className="flex items-center gap-2">
                  <MapPin className="size-4 text-primary/70" />
                  <span>Fort Lauderdale</span>
                </div>
              </div>
            </div>

            {}
            <div className="flex-1 p-6 md:p-10 flex flex-col justify-center relative">
              <div className="absolute top-0 left-0 w-1 h-0 bg-primary transition-all duration-500 group-hover:h-full"></div>
              
              <h3 className="text-2xl md:text-4xl font-serif uppercase tracking-wide text-foreground mb-4 group-hover:text-primary transition-colors duration-300">
                {post.title}
              </h3>
              
              <div className="text-muted-foreground mb-8 line-clamp-3 md:line-clamp-2 text-base md:text-lg leading-relaxed" dangerouslySetInnerHTML={{
            __html: post.excerpt || "Join us for this exclusive event in the heart of Fort Lauderdale. Experience the high-energy atmosphere and premium entertainment."
          }} />

              <div className="flex flex-wrap items-center gap-4 mt-auto">
                <Link to={post.link || "#"}>
                  <Button variant="default" size="lg" className="font-mono uppercase tracking-wider font-bold shadow-[0_0_15px_rgba(245,158,11,0.3)] hover:shadow-[0_0_25px_rgba(245,158,11,0.5)] transition-shadow">
                    <Ticket className="size-4 mr-2" />
                    Ticket Info
                  </Button>
                </Link>
                
                <Link to={generateGCalLink(post.title, post.excerpt, post.date)} newTab>
                  <Button variant="outline" size="lg" className="font-mono uppercase tracking-wider border-border/50 hover:border-primary/50 hover:bg-primary/10">
                    <CalendarPlus className="size-4 mr-2 text-primary" />
                    Export to GCal
                  </Button>
                </Link>
              </div>
            </div>
          </article>;
    })}
    </div>;
}
export default function UpcomingCityEvents() {
  const wp_query_params = {
    per_page: 5,
    orderby: "date",
    order: "desc"
  };
  const wp_query = new WP_Query(wp_query_params);
  return <section id="upcoming-city-events" className="dark bg-background text-foreground py-24 px-4 sm:px-6 lg:px-8 relative overflow-hidden">
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[800px] h-[400px] bg-primary/5 blur-[120px] rounded-full pointer-events-none"></div>

      <div className="max-w-7xl mx-auto relative z-10">
        <div className="text-center mb-16 md:mb-24">
          <h2 className="font-serif text-5xl md:text-7xl uppercase tracking-widest text-transparent bg-clip-text bg-gradient-to-r from-foreground via-primary to-foreground drop-shadow-[0_0_30px_rgba(245,158,11,0.2)] mb-4">
            City Events
          </h2>
          <p className="font-mono text-muted-foreground uppercase tracking-[0.3em] text-sm md:text-base">
            The Definitive Fort Lauderdale Schedule
          </p>
        </div>

        <PostsProvider wp_query={{
        per_page: 5,
        orderby: "date",
        order: "desc"
      }}>
          <EventsList />
        </PostsProvider>
      </div>
    </section>;
}